import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminAPI, ActiveUser } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { formatShortDate } from '../utils/dateUtils';

const ActiveUsers: React.FC = () => {
  const [users, setUsers] = useState<ActiveUser[]>([]);
  const [dates, setDates] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const { logout, user } = useAuth();

  useEffect(() => {
    loadActiveUsers();
  }, []);

  const loadActiveUsers = async () => {
    setIsLoading(true);
    setError('');
    try {
      const response = await adminAPI.getActiveUsers(5, 20);
      if (response.success) {
        setUsers(response.users);
        setDates(response.dates);
      } else {
        setError(response.error || 'Failed to load active users');
      }
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Only super admins can view the active users report.');
      } else {
        setError('Failed to load active users');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const getPercentage = (actual: number | null | undefined, target: number | null | undefined): number | null => {
    if (actual == null || target == null || target === 0) return null;
    return Math.round((actual / target) * 100);
  };

  const getPercentageColor = (pct: number | null): string => {
    if (pct === null) return 'text-gray-400';
    if (pct >= 90 && pct <= 110) return 'text-green-600 font-medium';
    if (pct >= 70 && pct <= 130) return 'text-amber-600';
    return 'text-red-600';
  };

  const hasMissingTargets = (u: ActiveUser) => {
    return u.targets.carbs == null || u.targets.protein == null || u.targets.fat == null;
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/dashboard')}
              className="text-gray-600 hover:text-gray-900"
            >
              &larr; Back
            </button>
            <h1 className="text-2xl font-bold text-gray-900">Active Users</h1>
          </div>
          <div className="flex items-center space-x-4">
            <span className="text-sm text-gray-600">{user?.email}</span>
            <button
              onClick={logout}
              className="text-sm text-red-600 hover:text-red-800"
            >
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Info */}
        <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-sm text-blue-800">
            Showing top 20 users by activity in the past 5 days.
            Percentages show how close each day's intake was to their targets
            <span className="text-green-600 font-medium"> (90-110% = green)</span>,
            <span className="text-amber-600"> (70-130% = amber)</span>,
            <span className="text-red-600"> (&lt;70% or &gt;130% = red)</span>.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {isLoading ? (
            <div className="p-8 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No active users found in the past 5 days
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider sticky left-0 bg-gray-50">
                      User
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Targets
                    </th>
                    <th className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Meals
                    </th>
                    {dates.map((date) => (
                      <th key={date} className="px-3 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                        {formatShortDate(date)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.map((activeUser) => (
                    <tr
                      key={activeUser.id}
                      className="hover:bg-gray-50 cursor-pointer"
                      onClick={() => navigate(`/users/${activeUser.id}`)}
                    >
                      <td className="px-4 py-3 whitespace-nowrap sticky left-0 bg-white">
                        <div className="flex items-center gap-2">
                          <div className="text-sm font-medium text-gray-900 truncate max-w-[200px]" title={activeUser.email}>
                            {activeUser.email}
                          </div>
                          {hasMissingTargets(activeUser) && (
                            <span className="px-1.5 py-0.5 text-xs bg-amber-100 text-amber-700 rounded flex-shrink-0" title="Missing macro targets">
                              !
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center">
                        {hasMissingTargets(activeUser) ? (
                          <span className="text-xs text-gray-400">Not set</span>
                        ) : (
                          <div className="text-xs">
                            <span className="text-blue-600">{activeUser.targets.carbs}c</span>
                            {' / '}
                            <span className="text-green-600">{activeUser.targets.protein}p</span>
                            {' / '}
                            <span className="text-amber-600">{activeUser.targets.fat}f</span>
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-3 whitespace-nowrap text-center text-sm text-gray-600">
                        {activeUser.totalMeals}
                      </td>
                      {activeUser.dailyData.map((day) => {
                        const carbsPct = day.totals ? getPercentage(day.totals.carbs, activeUser.targets.carbs) : null;
                        const proteinPct = day.totals ? getPercentage(day.totals.protein, activeUser.targets.protein) : null;
                        const fatPct = day.totals ? getPercentage(day.totals.fat, activeUser.targets.fat) : null;

                        return (
                          <td key={day.date} className="px-3 py-3 whitespace-nowrap text-center">
                            {day.totals ? (
                              <div className="text-xs space-y-0.5">
                                <div className={getPercentageColor(carbsPct)}>
                                  {carbsPct !== null ? `${carbsPct}%` : '-'}
                                </div>
                                <div className={getPercentageColor(proteinPct)}>
                                  {proteinPct !== null ? `${proteinPct}%` : '-'}
                                </div>
                                <div className={getPercentageColor(fatPct)}>
                                  {fatPct !== null ? `${fatPct}%` : '-'}
                                </div>
                              </div>
                            ) : (
                              <span className="text-xs text-gray-300">-</span>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Legend */}
        <div className="mt-4 text-xs text-gray-500">
          <p>Each day column shows: <span className="text-blue-600">carbs %</span> / <span className="text-green-600">protein %</span> / <span className="text-amber-600">fat %</span> of target</p>
        </div>
      </main>
    </div>
  );
};

export default ActiveUsers;
